import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { SubjectScheduleService } from 'src/app/services/api/subject-schedules/subject-schedule.service';
import { SubjectService } from 'src/app/services/api/subjects/subject.service';
import { UserManagementService } from 'src/app/services/api/user-management/user-management.service';
import { DepartmentModel } from 'src/app/services/models/department.model';
import {
  CurriculumType,
  SubjectCreateOption,
  SubjectTypeOption,
  TeacherOption,
  TeachingTypeOption,
} from 'src/app/services/models/subjects/subject.model';
import {
  ProjectResearchSubjectDetail,
  ProjectResearchSubjectUpdatePayload,
  ScheduleSubmissionUserOption,
  SubjectScheduleGroupCreatePayload,
  SubjectScheduleGroupCreateResponse,
  SubjectOfferingCreatePayload,
  SubjectOfferingCreateResponse,
} from 'src/app/services/models/subject-schedules/subject-schedule.model';
import { AcademicTermModel } from 'src/app/services/models/study-period/study-period.model';

export type ScheduleSubmissionUsersByUserId = Partial<Record<number, ScheduleSubmissionUserOption>>;

@Injectable({ providedIn: 'root' })
export class SubjectManagementCreateFacade {
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly subjectScheduleService = inject(SubjectScheduleService);
  private readonly subjectService = inject(SubjectService);
  private readonly userManagementService = inject(UserManagementService);

  createSubjectScheduleGroup(
    payload: SubjectScheduleGroupCreatePayload
  ): Observable<SubjectScheduleGroupCreateResponse> {
    return this.subjectScheduleService.createSubjectScheduleGroup(payload);
  }

  createSubjectOffering(
    payload: SubjectOfferingCreatePayload
  ): Observable<SubjectOfferingCreateResponse> {
    return this.subjectScheduleService.createSubjectOffering(payload);
  }

  saveSubjectScheduleGroupDraft(
    persistedId: number | null,
    payload: SubjectScheduleGroupCreatePayload,
    isProjectResearch: boolean,
  ): Observable<SubjectScheduleGroupCreateResponse | ProjectResearchSubjectDetail> {
    if (isProjectResearch && persistedId != null) {
      const body = payload.subject_schedule_group;
      const firstSection = body.sections[0];
      const section = typeof firstSection === 'string'
        ? firstSection
        : firstSection.section;
      const projectPayload: ProjectResearchSubjectUpdatePayload = {
        project_research_subject: {
          academic_term_id: body.academic_term_id,
          subject_id: body.subject_id,
          curriculum_type_id: body.curriculum_type_id,
          courseunit: body.courseunit,
          section: String(section),
          status: 'draft',
          replace_slots: body.replace_slots,
          assignments: body.assignments ?? [],
        },
      };

      return this.subjectScheduleService.updateProjectResearchSubject(persistedId, projectPayload);
    }

    if (persistedId != null) {
      return this.subjectScheduleService.updateSubjectScheduleGroup(persistedId, payload);
    }

    return this.subjectScheduleService.createSubjectScheduleGroup(payload);
  }

  getAcademicTerms(name: string | null, year: string | null): Observable<AcademicTermModel[]> {
    return this.studyPeriodService.getAcademicTerms({
      summary: true,
      name,
      year,
      page: 1,
      limit: 1,
    });
  }

  getCurriculumTypeOptions(): Observable<CurriculumType[]> {
    return this.subjectService.getCurriculumTypes({ datatable: false });
  }

  getStudyModeOptions(): Observable<SubjectTypeOption[]> {
    return this.subjectScheduleService.getStudyModes().pipe(
      map((studyModes) => studyModes
        .map((studyMode): SubjectTypeOption | null => {
          const label = String(studyMode.name_th || studyMode.name_en || studyMode.code || '').trim();
          return label === '' ? null : { id: studyMode.id, label, code: studyMode.code };
        })
        .filter((option): option is SubjectTypeOption => option != null))
    );
  }

  getSubjectOptions(academicTermId: number): Observable<SubjectCreateOption[]> {
    return this.subjectService.getSubjectsList({
      academic_term_id: academicTermId,
      // Every subject in the term is selectable — including used synced (REG) rows:
      // a used รหัสวิชา may be reused for a duplicate offering (different ประเภท,
      // คำอธิบายหน่วยกิต or หลักสูตร). A fully identical snapshot is rejected by the
      // offering-level guards on create.
      datatable: false,
    }).pipe(
      map((response) => this.dedupeManualSubjectOptions(
        response.data.map((subject) => ({
          id: subject.id,
          code: subject.code,
          displayLabel: this.getSubjectOptionDisplayLabel(subject.code, subject.courseunit),
          name: subject.name,
          curriculum: subject.curriculum_type?.name ?? '',
          curriculumTypeId: subject.curriculum_type_id,
          courseunit: subject.courseunit ?? '',
          teachingWorkloadHours: subject.teaching_workload_hours,
          isSynced: subject.is_synced,
        }))
      ))
    );
  }

  getTeachingTypeOptions(): Observable<TeachingTypeOption[]> {
    return this.subjectService.getTeachingType().pipe(
      map((teachingTypes) => teachingTypes
        .map((teachingType): TeachingTypeOption | null => {
          const label = String(teachingType.name_th).trim();
          return label === '' ? null : { id: teachingType.id, label, code: teachingType.code };
        })
        .filter((option): option is TeachingTypeOption => option != null))
    );
  }

  getTeacherOptions(): Observable<TeacherOption[]> {
    return this.subjectScheduleService.getUsersRolseTeacherList({datatable:false}).pipe(
      map((response) => response.data.map((user) => ({
        id: user.id,
        fullName: user.full_name,
        department: this.getDepartmentLabel(user.department),
      })))
    );
  }

  getScheduleSubmissionUsersByUserId(academicTermId: number): Observable<ScheduleSubmissionUsersByUserId> {
    return this.subjectScheduleService.getScheduleSubmissionUsers({ academic_term_id: academicTermId }).pipe(
      map((response) => response.data.reduce<ScheduleSubmissionUsersByUserId>((optionsByUserId, option) => {
        optionsByUserId[option.user_id] = option;
        return optionsByUserId;
      }, {}))
    );
  }

  // A manual รหัสวิชา may exist as several rows (legacy variants from the old
  // write-through flow), but the picker only needs one entry per code — the entered
  // หลักสูตร / คำอธิบายหน่วยกิต now live on the offering itself, never on the master
  // subject. Keep one representative per manual code, preferring a blank-courseunit
  // template so its fallback doesn't leak a sibling variant's value. Synced rows
  // pass through untouched (each REG course stays individually selectable).
  private dedupeManualSubjectOptions(options: SubjectCreateOption[]): SubjectCreateOption[] {
    const result: SubjectCreateOption[] = [];
    const manualIndexByCode = new Map<string, number>();

    for (const option of options) {
      if (option.isSynced === true) {
        result.push(option);
        continue;
      }

      const existingIndex = manualIndexByCode.get(option.code);
      if (existingIndex == null) {
        manualIndexByCode.set(option.code, result.length);
        result.push(option);
        continue;
      }

      // Prefer an unused template (blank courseunit) over an already-used variant.
      const existing = result[existingIndex];
      if ((existing.courseunit ?? '') !== '' && (option.courseunit ?? '') === '') {
        result[existingIndex] = option;
      }
    }

    return result;
  }

  private getDepartmentLabel(department: DepartmentModel | string | null | undefined): string {
    if (typeof department === 'string') {
      const label = department.trim();
      return label !== '' ? label : '-';
    }

    const label = (department?.name_th ?? department?.name ?? department?.name_en ?? '').trim();
    return label !== '' ? label : '-';
  }

  private getSubjectOptionDisplayLabel(code: string, courseunit?: string | null): string {
    const subjectCode = code.trim();
    const creditDescription = String(courseunit ?? '').trim();

    return creditDescription === '' ? subjectCode : `${subjectCode}(${creditDescription})`;
  }
}
